import React, { useState, useEffect, useRef } from 'react';
import Modal from '../../../components/ui/Modal';
import ExternalSourcePicker from '../../external-sources/components/ExternalSourcePicker';
import { FormActions, FormError, FormField, FormIntro } from '../../../components/ui/FormModalParts';
import { splitTrimmed } from '../../../utils/text';
import { getExternalId } from '../../external-sources/utils/externalIds';

const STATUS_OPTIONS = [
  ['UPCOMING', 'Upcoming'],
  ['ONGOING', 'Ongoing'],
  ['COMPLETED', 'Completed'],
  ['CANCELLED', 'Cancelled'],
  ['ARCHIVED', 'Archived'],
];

const buildFormDataFromEvent = (event) => ({
  title: event?.title || '',
  description: event?.description || '',
  status: event?.status || 'UPCOMING',
  tags: Array.isArray(event?.tags) ? event.tags.join(', ') : '',
  image_url: event?.image_url || event?.imageUrl || '',
  wp_id: getExternalId(event, 'wp_id', 'wpId'),
  faicon_id: getExternalId(event, 'faicon_id', 'faiconId'),
  chatbot_id: getExternalId(event, 'chatbot_id', 'chatbotId'),
  missions: Boolean(event?.missions),
});

const buildUpdateData = (formData) => {
  const tags = splitTrimmed(formData.tags, ',');
  const wpId = formData.wp_id.trim();
  const faiconId = formData.faicon_id.trim();
  return {
    title: formData.title.trim(),
    description: formData.description.trim() || null,
    status: formData.status,
    tags: tags.length > 0 ? tags : null,
    image_url: formData.image_url.trim() || null,
    ...(wpId ? { wp_id: wpId } : {}),
    ...(faiconId ? { faicon_id: faiconId } : {}),
    chatbot_id: formData.chatbot_id.trim(),
    missions: Boolean(formData.missions),
  };
};

/** Pick an event from an external catalog, or type its ID into `name`. */
function LinkedEventField({ source, name, label, hint, pickerKey, formData, onPick, onChange, disabled }) {
  return (
    <div className="event-modal-subsection">
      <span className="event-modal-subsection-title">{source}</span>
      <ExternalSourcePicker
        key={pickerKey}
        mode="event"
        source={source}
        showSourceToggle={false}
        linkedEventId={formData[name]}
        onSelectionChange={(selection) => onPick(name, selection.eventId || '')}
        disabled={disabled}
        compact
      />
      <FormField id={name} label={label} hint={hint}>
        <input
          id={name}
          name={name}
          type="text"
          value={formData[name]}
          onChange={onChange}
          className="event-modal-control"
          placeholder="Filled automatically when you pick an event above"
          disabled={disabled}
        />
      </FormField>
    </div>
  );
}

const EditEventModal = ({ isOpen, onClose, onSave, event }) => {
  const [formData, setFormData] = useState(() => buildFormDataFromEvent(null));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formSession, setFormSession] = useState(0);
  const wasOpenRef = useRef(false);

  // Reset form from saved event when the modal opens (discard unsaved edits).
  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;
    wasOpenRef.current = isOpen;

    if (!justOpened || !event) return;
    setFormData(buildFormDataFromEvent(event));
    setError('');
    setFormSession((prev) => prev + 1);
  }, [isOpen, event]);

  const setField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleChange = (e) => setField(e.target.name, e.target.value);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await onSave(buildUpdateData(formData));
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to update event');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError('');
      onClose();
    }
  };

  const linkedEventProps = { formData, onPick: setField, onChange: handleChange, disabled: loading };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Edit Event" size="lg" className="event-modal">
      <form onSubmit={handleSubmit} className="event-modal-form">
        <FormIntro title={event?.title || 'Event details'}>
          Update the event metadata used across event lists, details, and report generation.
        </FormIntro>

        <FormField id="title" label={<>Title <strong>*</strong></>}>
          <input
            id="title"
            name="title"
            type="text"
            value={formData.title}
            onChange={handleChange}
            className="event-modal-control"
            placeholder="Enter event title"
            disabled={loading}
            autoFocus
          />
        </FormField>

        <FormField id="description" label="Description">
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={3}
            className="event-modal-control"
            placeholder="Enter event description"
            disabled={loading}
          />
        </FormField>

        <FormField id="status" label={<>Status <strong>*</strong></>}>
          <select
            id="status"
            name="status"
            value={formData.status}
            onChange={handleChange}
            className="event-modal-control"
            disabled={loading}
          >
            {STATUS_OPTIONS.map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </FormField>

        <FormField id="tags" label="Tags" hint="Separate multiple tags with commas, for example: conference, diplomacy, UN.">
          <input
            id="tags"
            name="tags"
            type="text"
            value={formData.tags}
            onChange={handleChange}
            className="event-modal-control"
            placeholder="Enter tags separated by commas"
            disabled={loading}
          />
        </FormField>

        <FormField id="image_url" label="Image URL">
          <input
            id="image_url"
            name="image_url"
            type="url"
            value={formData.image_url}
            onChange={handleChange}
            className="event-modal-control"
            placeholder="https://example.com/image.jpg"
            disabled={loading}
          />
        </FormField>

        <div className="event-modal-section">
          <span className="event-modal-section-title">External links</span>
          <p className="event-modal-section-note">
            Connect this event to WordPress or Faicon so the official report can be published there.
            Missions is separate: custom reports go there as JSON, one country at a time.
          </p>

          <label className="event-modal-option">
            <input
              type="checkbox"
              checked={formData.missions}
              onChange={(e) => setField('missions', e.target.checked)}
              disabled={loading}
            />
            <span>
              <span className="event-modal-option-head">Missions</span>
              <span className="event-modal-section-note">
                Send custom reports to the missions endpoint. DigWatch and Faicon links stay for the official report.
              </span>
            </span>
          </label>

          <LinkedEventField
            {...linkedEventProps}
            source="WordPress"
            name="wp_id"
            label="WordPress event ID"
            hint="Used when publishing the event report to WordPress (DigWatch)."
            pickerKey={`event-wp-${formSession}`}
          />
          <LinkedEventField
            {...linkedEventProps}
            source="Faicon"
            name="faicon_id"
            label="Faicon event ID"
            hint="Faicon event UUID. Used when sending event reports to Faicon."
            pickerKey={`event-faicon-${formSession}`}
          />

          <div className="event-modal-subsection">
            <span className="event-modal-subsection-title">MyDepartment chatbot</span>
            <FormField
              id="chatbot_id"
              label="Chatbot ID or public URL"
              hint="Filled when a session report is published to DigWatch or FAICON. That publish replaces this with the MyDepartment bot for this event and embeds the same bot."
            >
              <input
                id="chatbot_id"
                name="chatbot_id"
                type="text"
                value={formData.chatbot_id}
                onChange={handleChange}
                className="event-modal-control"
                placeholder="e.g. 9a665f91-baef-4e60-8b0d-174f73d9515e"
                disabled={loading}
              />
            </FormField>
          </div>
        </div>

        <FormError message={error} />

        <FormActions onCancel={handleClose} disabled={loading}>
          {loading ? 'Saving...' : 'Save Changes'}
        </FormActions>
      </form>
    </Modal>
  );
};

export default EditEventModal;
